import React from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  Tooltip,
  useTheme,
} from '@mui/material';
import { useFavorites } from '../hooks/useFavorites';
import { CustomTextField } from '../../../../Common/Components/Controls/InputControls';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import BookmarkAddOutlinedIcon from '@mui/icons-material/BookmarkAddOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { NewFavoriteQuery } from '../types/query';
import { baseIconButtonStyle, getColorSet } from '../utils/iconStyles';

interface QueryToolbarProps {
  onExecute: () => void;
  onDownload: () => void;
  onToggleFavorites: () => void;
  favoriteQuery: NewFavoriteQuery;
  setFavoriteQuery: (query: NewFavoriteQuery) => void;
  loading: boolean;
  setError: (value: React.SetStateAction<string>) => void;
}

const QueryToolbar: React.FC<QueryToolbarProps> = ({
  onExecute,
  onDownload,
  onToggleFavorites,
  favoriteQuery,
  setFavoriteQuery,
  loading,
  setError,
}) => {
  const { addFavorite } = useFavorites({ setError });
  const theme = useTheme();

  const handleSaveFavorite = async () => {
    if (!favoriteQuery?.QueryText.trim() || !favoriteQuery.QueryName.trim()) {
      setError('Query name and query cannot be empty.');
      return;
    }

    await addFavorite(favoriteQuery);
    handleNewQuery();
  };

  const handleNewQuery = () => {
    setFavoriteQuery({
      Id: '00000000-0000-0000-0000-000000000000',
      QueryName: '',
      QueryText: '',
    });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
      >
        <CustomTextField
          label="Query Name"
          value={favoriteQuery.QueryName}
          onChange={(e) =>
            setFavoriteQuery({ ...favoriteQuery, QueryName: e.target.value })
          }
          size="small"
          sx={{ minWidth: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EditNoteIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Tooltip title="Add Query to Favorites">
          <IconButton
            onClick={handleSaveFavorite}
            sx={{ ...baseIconButtonStyle, ...getColorSet(theme, 'primary') }}
          >
            <BookmarkAddOutlinedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Run Query">
          <IconButton
            onClick={onExecute}
            disabled={loading}
            sx={{ ...baseIconButtonStyle, ...getColorSet(theme, 'success') }}
          >
            <PlayArrowRoundedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="New Query">
          <IconButton
            onClick={handleNewQuery}
            sx={{ ...baseIconButtonStyle, ...getColorSet(theme, 'info') }}
          >
            <AddCircleRoundedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Download Results in XLSX">
          <IconButton
            onClick={onDownload}
            sx={{ ...baseIconButtonStyle, ...getColorSet(theme, 'warning') }}
          >
            <DownloadRoundedIcon />
          </IconButton>
        </Tooltip>

        <Tooltip title="Toggle Favorites">
          <IconButton
            onClick={onToggleFavorites}
            sx={{ ...baseIconButtonStyle, ...getColorSet(theme, 'error') }}
          >
            <FavoriteBorderRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

export default QueryToolbar;
